import { useCallback, useRef } from "react";
import { useApiClient } from "@/hooks/useApiClient";
import {
  shouldValidateZenrinWord,
  validateZenrinWordQuery,
} from "@/features/map/utils/zenrinWordValidation";
import type { ZenrinWordMatchType } from "@/features/map/utils/zenrinWordMatchType";
import { throwIfZenrinRateLimited } from "@/features/map/utils/zenrinRateLimit";
import { type GeoSpaceChiban } from "../types";

export {
  ZENRIN_WORD_MATCH_TYPE,
  type ZenrinWordMatchType,
} from "@/features/map/utils/zenrinWordMatchType";
export { validateZenrinWordQuery } from "@/features/map/utils/zenrinWordValidation";

type ZenrinResponseStatus = "success" | "failed";

interface ZenrinChibanResponseItem {
  prefName: string;
  cityName: string;
  ooazaName: string;
  azaName: string;
  chiban: string;
  addressChiban: string;
  lat: number | null;
  lng: number | null;
  address: string;
  area: number | null;
}

interface ZenrinSearchResponse {
  status: ZenrinResponseStatus;
  chiban: ZenrinChibanResponseItem[];
}

interface ZenrinSearchParams {
  chiban: string;
  word_match_type?: ZenrinWordMatchType;
}

interface ZenrinReverseParams {
  address: string;
}

interface ZenrinReverseLatLngParams {
  lat: number;
  lng: number;
}

interface ZenrinReverseCircleParams extends ZenrinReverseLatLngParams {
  radius: number;
}

interface ZenrinReversePolygonParams {
  polygon: string | undefined;
}

// /zenrin/reverse-polygon は GEOSPACE の reverse-polygon と同一インターフェース
// （リクエスト/レスポンスとも同形。URL の geospace が zenrin に変わるだけ）
interface ZenrinReversePolygonResponse {
  status: string;
  error_message: string;
  chiban: GeoSpaceChiban[];
}

interface ZenrinOAuthTokenResponse {
  accessToken: string;
  tokenType: string;
  expiredAt: string;
}

interface ZenrinMapImageRequestParams {
  bbox: string;
  zoomLevel: number;
  width: number;
  height: number;
  mapType: string;
}

interface ZenrinMapImageRequestResponse {
  requestId: number;
  imageUuid: string;
  minLongitude: number;
  minLatitude: number;
  maxLongitude: number;
  maxLatitude: number;
  centerLongitude: number;
  centerLatitude: number;
  address: string;
  zoomLevel: number;
  width: number;
  height: number;
  mapType: string;
  imageUrl: string;
  createdAt: string;
}

export interface ZenrinChiban {
  prefName: string;
  cityName: string;
  ooazaName: string;
  azaName: string;
  chiban: string;
  addressChiban: string;
  lat: number | null;
  lng: number | null;
  address: string;
  area: number | null;
}

export interface ZenrinOAuthToken {
  accessToken: string;
  tokenType: string;
  expiredAt: string;
}

export interface ZenrinMapImageRequest {
  requestId: number;
  imageUuid: string;
  minLongitude: number;
  minLatitude: number;
  maxLongitude: number;
  maxLatitude: number;
  centerLongitude: number;
  centerLatitude: number;
  address: string;
  zoomLevel: number;
  width: number;
  height: number;
  mapType: string;
  imageUrl: string;
  createdAt: string;
}

/** 残り無料取得回数APIのレスポンス（axios-case-converter によりキャメルケース） */
export interface ZenrinRemainingImageCountResponse {
  remainingFreeCount: number;
}

/** ゼンリン画像1枚あたり単価APIのレスポンス（axios-case-converter によりキャメルケース） */
export interface ZenrinImageRequestPriceResponse {
  unitPrice: number;
}

/**
 * 検索系 API（zenrinSearch / zenrinReverseBy*）は、利用者単位レート制限（HTTP 429）のときだけ
 * `ZenrinRateLimitError` を throw する。それ以外のエラーは従来どおり握りつぶして
 * undefined / [] を返す。呼び出し側は `isZenrinRateLimitError` で判別してトーストを出すこと。
 */
export interface ZenrinApi {
  zenrinSearch: (
    chiban: string,
    wordMatchType?: ZenrinWordMatchType
  ) => Promise<ZenrinChiban | undefined>;
  zenrinSearchByReceptionBook: (
    bookId: number
  ) => Promise<ZenrinChiban | undefined>;
  zenrinReverseByAddress: (
    address: string
  ) => Promise<ZenrinChiban | undefined>;
  zenrinReverseByLatLng: (
    lat: number,
    lng: number
  ) => Promise<ZenrinChiban | undefined>;
  zenrinReverseByCircle: (
    lat: number,
    lng: number,
    radius: number,
    signal?: AbortSignal
  ) => Promise<ZenrinChiban[]>;
  zenrinReverseByPolygon: (
    polygon: string | undefined,
    signal?: AbortSignal
  ) => Promise<GeoSpaceChiban[] | undefined>;
  zenrinGetOAuthToken: () => Promise<ZenrinOAuthToken | undefined>;
  zenrinRequestMapImage: (
    bbox: string,
    zoomLevel: number,
    width: number,
    height: number,
    mapType: string
  ) => Promise<ZenrinMapImageRequest | undefined>;
  zenrinGetMapImage: (uuid: string) => Promise<Blob | undefined>;
  zenrinGetRemainingImageCount: () => Promise<
    ZenrinRemainingImageCountResponse | undefined
  >;
  zenrinGetImageRequestPrice: () => Promise<
    ZenrinImageRequestPriceResponse | undefined
  >;
}

const STATUS_SUCCESS: ZenrinResponseStatus = "success";

const convertChiban = (item: ZenrinChibanResponseItem): ZenrinChiban => ({
  prefName: item.prefName,
  cityName: item.cityName,
  ooazaName: item.ooazaName,
  azaName: item.azaName,
  chiban: item.chiban,
  addressChiban: item.addressChiban,
  lat: item.lat,
  lng: item.lng,
  address: item.address,
  area: item.area,
});

const extractFirstChiban = (
  response: ZenrinSearchResponse
): ZenrinChiban | undefined => {
  if (response.status !== STATUS_SUCCESS || response.chiban.length === 0) {
    return undefined;
  }
  return convertChiban(response.chiban[0]);
};

const extractChibanList = (response: ZenrinSearchResponse): ZenrinChiban[] => {
  if (response.status !== STATUS_SUCCESS || response.chiban.length === 0) {
    return [];
  }
  return response.chiban.map(convertChiban);
};

export const zenrinToGeoSpaceChiban = (
  item: ZenrinChiban
): GeoSpaceChiban | undefined => {
  if (item.lat == null || item.lng == null) {
    return undefined;
  }

  return {
    prefName: item.prefName,
    cityName: item.cityName,
    ooazaName: item.ooazaName ?? null,
    azaName: item.azaName ?? null,
    chiban: item.chiban,
    addressChiban: item.addressChiban,
    polygon: undefined,
    lat: item.lat,
    lng: item.lng,
    address: item.address,
    area: item.area ?? null,
  };
};

export const zenrinListToGeoSpaceChiban = (
  items: ZenrinChiban[]
): GeoSpaceChiban[] =>
  items
    .map((item) => zenrinToGeoSpaceChiban(item))
    .filter((item): item is GeoSpaceChiban => item !== undefined);

export const useZenrinApi = (): ZenrinApi => {
  const { apiClient } = useApiClient();
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;

  const zenrinSearch = async (
    chiban: string,
    wordMatchType?: ZenrinWordMatchType
  ): Promise<ZenrinChiban | undefined> => {
    if (
      shouldValidateZenrinWord(wordMatchType) &&
      !validateZenrinWordQuery(chiban).ok
    ) {
      return undefined;
    }

    const params: ZenrinSearchParams = {
      chiban,
      ...(wordMatchType !== undefined && { word_match_type: wordMatchType }),
    };

    try {
      const response = await apiClient.get<ZenrinSearchResponse>(
        "/zenrin/search",
        { params }
      );
      return extractFirstChiban(response.data);
    } catch (error) {
      throwIfZenrinRateLimited(error);
      return undefined;
    }
  };

  /**
   * 受付帳から地図へプロットするための地番検索。
   * 検索語は BE が受付帳の行から組み立てる（完全一致）。地図操作とは別のレート制限枠で数えられる。
   * 受付帳が無い・契約エリア外・土地以外（404）など、取得できない場合は undefined を返す。
   */
  const zenrinSearchByReceptionBook = async (
    bookId: number
  ): Promise<ZenrinChiban | undefined> => {
    try {
      const response = await apiClient.get<ZenrinSearchResponse>(
        `/zenrin/real-estate-books/${bookId}/location`
      );
      return extractFirstChiban(response.data);
    } catch (error) {
      throwIfZenrinRateLimited(error);
      return undefined;
    }
  };

  const zenrinReverseByAddress = async (
    address: string
  ): Promise<ZenrinChiban | undefined> => {
    if (!validateZenrinWordQuery(address).ok) {
      return undefined;
    }

    const params: ZenrinReverseParams = { address };

    try {
      const response = await apiClient.get<ZenrinSearchResponse>(
        "/zenrin/reverse",
        { params }
      );
      return extractFirstChiban(response.data);
    } catch (error) {
      throwIfZenrinRateLimited(error);
      return undefined;
    }
  };

  const zenrinReverseByLatLng = async (
    lat: number,
    lng: number
  ): Promise<ZenrinChiban | undefined> => {
    const params: ZenrinReverseLatLngParams = { lat, lng };

    try {
      const response = await apiClient.get<ZenrinSearchResponse>(
        "/zenrin/reverse-latlng",
        { params }
      );
      return extractFirstChiban(response.data);
    } catch (error) {
      throwIfZenrinRateLimited(error);
      return undefined;
    }
  };

  const zenrinReverseByCircle = async (
    lat: number,
    lng: number,
    radius: number,
    signal?: AbortSignal
  ): Promise<ZenrinChiban[]> => {
    const params: ZenrinReverseCircleParams = {
      lat,
      lng,
      radius: Math.ceil(radius),
    };

    try {
      const response = await apiClient.get<ZenrinSearchResponse>(
        "/zenrin/reverse-circle",
        { params, signal }
      );
      return extractChibanList(response.data);
    } catch (error) {
      throwIfZenrinRateLimited(error);
      return [];
    }
  };

  // ポリゴン（矩形含む）範囲検索。GEOSPACE の reverse-polygon と同一インターフェースで、
  // URL の geospace が zenrin に変わるだけ。
  const zenrinReverseByPolygon = async (
    polygon: string | undefined,
    signal?: AbortSignal
  ): Promise<GeoSpaceChiban[] | undefined> => {
    const params: ZenrinReversePolygonParams = { polygon };

    try {
      const response = await apiClient.get<ZenrinReversePolygonResponse>(
        "/zenrin/reverse-polygon",
        { params, signal }
      );
      if (response.data.status === "success") {
        return response.data.chiban;
      }
      return undefined;
    } catch (error) {
      throwIfZenrinRateLimited(error);
      return undefined;
    }
  };

  const zenrinGetOAuthToken = async (): Promise<
    ZenrinOAuthToken | undefined
  > => {
    try {
      const response = await apiClient.post<ZenrinOAuthTokenResponse>(
        "/zenrin/oauth/token"
      );
      return response.data;
    } catch (_error) {
      return undefined;
    }
  };

  const zenrinRequestMapImage = async (
    bbox: string,
    zoomLevel: number,
    width: number,
    height: number,
    mapType: string
  ): Promise<ZenrinMapImageRequest | undefined> => {
    const params: ZenrinMapImageRequestParams = {
      bbox,
      zoomLevel,
      width,
      height,
      mapType,
    };

    try {
      const response = await apiClient.post<ZenrinMapImageRequestResponse>(
        "/zenrin/image-request",
        params
      );
      return response.data;
    } catch (_error) {
      return undefined;
    }
  };

  const zenrinGetMapImage = async (uuid: string): Promise<Blob | undefined> => {
    try {
      const response = await apiClient.get<Blob>(
        `/zenrin/image-request/${uuid}/image`,
        {
          responseType: "blob",
        }
      );
      return response.data;
    } catch (_error) {
      return undefined;
    }
  };

  const zenrinGetRemainingImageCount = useCallback(async (): Promise<
    ZenrinRemainingImageCountResponse | undefined
  > => {
    try {
      const response =
        await apiClientRef.current.get<ZenrinRemainingImageCountResponse>(
          "/zenrin/image-request/budget"
        );
      return response.data;
    } catch (_error) {
      return undefined;
    }
  }, []);

  const zenrinGetImageRequestPrice = useCallback(async (): Promise<
    ZenrinImageRequestPriceResponse | undefined
  > => {
    try {
      const response =
        await apiClientRef.current.get<ZenrinImageRequestPriceResponse>(
          "/zenrin/image-request/price"
        );
      return response.data;
    } catch (_error) {
      return undefined;
    }
  }, []);

  return {
    zenrinSearch,
    zenrinSearchByReceptionBook,
    zenrinReverseByAddress,
    zenrinReverseByLatLng,
    zenrinReverseByCircle,
    zenrinReverseByPolygon,
    zenrinGetOAuthToken,
    zenrinRequestMapImage,
    zenrinGetMapImage,
    zenrinGetRemainingImageCount,
    zenrinGetImageRequestPrice,
  };
};
